import { Seo } from "@/components/Seo";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LoginForm } from "@/components/auth/LoginForm";
import { RegisterForm } from "@/components/auth/RegisterForm";

const SOCIAL = [
  { id: "facebook", label: "Facebook", icon: Facebook },
  { id: "instagram", label: "Instagram", icon: Instagram },
  { id: "x", label: "X", icon: Twitter },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin },
];

const googleLogin = (next) => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + (next || "/studio"))}`;
};

export default function Auth() {
  const [params] = useSearchParams();
  const adminMode = params.get("mode") === "admin";
  const [tab, setTab] = useState(params.get("mode") === "signup" ? "signup" : "signin");
  const next = params.get("next");
  const referral = params.get("ref") || "";

  return (
    <div className="min-h-screen bg-[#07080E] text-slate-100 relative overflow-hidden grain">
      <Seo title="Sign in to Vectra" description="Sign in or create your Vectra account." noindex />
      <div className="absolute inset-0 grid-bg opacity-30" />
      <nav className="relative z-20 h-16 px-6 sm:px-10 flex items-center justify-between border-b border-white/10">
        <Link to="/"><Logo size={34} /></Link>
        <Link to="/pricing" className="text-sm text-slate-400 hover:text-white">Pricing</Link>
      </nav>
      <div className="relative z-10 px-6 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-[1fr_520px] gap-10 max-w-6xl mx-auto items-start">
        <motion.div initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} className="hidden lg:block pt-10">
          <div className="text-xs font-mono uppercase tracking-widest text-purple-300 mb-4">Create Cinematic Magic</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-[1.05]">One account.<br /><span className="text-gradient">37 AI engines.</span></h1>
          <p className="mt-5 text-slate-400 max-w-md leading-relaxed">Prepaid packs via UPI, no auto-debit. Verified WhatsApp number keeps your render alerts and renewal reminders flowing.</p>
          <ul className="mt-8 space-y-2 text-sm text-slate-300">
            {["Cinematic video, voice & dubbing, images", "Copyright-free license available", "Referral bonuses · +7 days for you and a friend"].map((t) => <li key={t} className="flex items-center gap-2"><Sparkles size={13} className="text-cyan-400" /> {t}</li>)}
          </ul>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} data-testid="auth-card" className="glass rounded-3xl p-6 sm:p-8">
          {adminMode && <div data-testid="admin-mode-banner" className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-2 text-xs text-amber-200 font-mono">Admin Command Center · founder / admin accounts only</div>}
          <div className={`flex rounded-full bg-white/5 p-1 mb-6 ${adminMode ? "hidden" : ""}`}>
            {[["signin", "Sign in"], ["signup", "Create account"]].map(([k, l]) => (
              <button key={k} data-testid={`auth-tab-${k}`} onClick={() => setTab(k)} className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${tab === k ? "bg-gradient-to-r from-purple-600 to-cyan-600 text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>

          <button data-testid="google-login-button" onClick={() => googleLogin(next)} className="w-full py-3 rounded-full text-sm font-semibold border border-white/15 hover:bg-white/5 inline-flex items-center justify-center gap-3 transition-colors">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.5 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.4 5.7c4.3-4 7.2-9.9 7.2-17.4z"/><path fill="#FBBC05" d="M10.4 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.8-6C.9 16.6 0 20.2 0 24s.9 7.4 2.6 10.7l7.8-6z"/><path fill="#34A853" d="M24 48c6.2 0 11.6-2 15.4-5.6l-7.4-5.7c-2 1.4-4.7 2.3-8 2.3-6.3 0-11.7-4.1-13.6-9.8l-7.8 6C6.5 42.6 14.6 48 24 48z"/></svg>
            Continue with Google
          </button>
          <div className="grid grid-cols-4 gap-2 mt-3">
            {SOCIAL.map((s) => (
              <button key={s.id} data-testid={`social-${s.id}`} disabled title={`${s.label} sign-in coming soon`} className="py-2.5 rounded-xl border border-white/10 text-slate-500 inline-flex flex-col items-center gap-1 cursor-not-allowed opacity-70">
                <s.icon size={16} /><span className="text-[9px] font-mono uppercase">Soon</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 my-6 text-[10px] font-mono uppercase tracking-widest text-slate-600"><div className="flex-1 h-px bg-white/10" /> or with email <div className="flex-1 h-px bg-white/10" /></div>

          {tab === "signin" || adminMode ? <LoginForm next={adminMode ? "/admin" : next} adminOnly={adminMode} /> : <RegisterForm next={next} referral={referral} />}
        </motion.div>
      </div>
    </div>
  );
}
